/**
 * In-memory handoff/session state for TimedTestTimer (not a remaining-ms cache).
 * Adaptive review uses awaited Firestore flush for item ↔ review continuity.
 * - timeEndedAlertByUtaId / autoSubmitTriggeredByUtaId: section expiry handoff
 * - sectionTimerHandoffByUtaId: manual/auto section submit — skip timeSpent writes
 *   that would clobber processSectionStatus's { timeSpent: 0 } reset
 * - flushTimeSpent: adaptive awaits Firestore before navigate
 */
const timeEndedAlertByUtaId = {}
const autoSubmitTriggeredByUtaId = {}
const sectionTimerHandoffByUtaId = {}

/** @type {null | (() => Promise<void>)} */
let flushTimeSpent = null

/**
 * Adaptive section tests: register a flush that awaits Firestore timeSpent write
 * so item ↔ review navigation does not remount against a stale doc.
 */
export function registerTimedTestTimeSpentFlush(fn) {
  flushTimeSpent = typeof fn === 'function' ? fn : null
}

/**
 * The player route is matched non-exactly, so item -> item navigation keeps
 * AssessmentPlayer (and TimedTestTimer) mounted. Only leaving the player for the
 * review page or the sections hub remounts the timer, which is the only case
 * where the countdown has to reach Firestore before the new mount reads the doc.
 */
export function navigationRemountsTimedTestTimer(urlToGo = '') {
  return /\/(test-summary|sections-start)(\/|$)/.test(urlToGo)
}

const FLUSH_TIMEOUT_MS = 2000

export function flushTimedTestTimeSpent() {
  if (!flushTimeSpent) {
    return Promise.resolve()
  }
  // Firestore resolves a write only on server ack, so it stays pending
  // indefinitely while offline. Navigation must never be blocked on that.
  return Promise.race([
    Promise.resolve()
      .then(() => flushTimeSpent())
      .catch(() => {}),
    new Promise((resolve) => {
      setTimeout(resolve, FLUSH_TIMEOUT_MS)
    }),
  ])
}

export function setTimeEndedAlertOpen(utaId, open) {
  if (utaId != null) {
    timeEndedAlertByUtaId[utaId] = open
  }
}

export function isTimeEndedAlertOpen(utaId) {
  return utaId != null && !!timeEndedAlertByUtaId[utaId]
}

export function clearTimeEndedAlertOpen(utaId) {
  if (utaId != null) {
    delete timeEndedAlertByUtaId[utaId]
    delete autoSubmitTriggeredByUtaId[utaId]
  }
}

/**
 * Mark section submit handoff so TimedTestTimer does not write prior-section
 * timeSpent over the server reset (manual submit unmount race).
 */
export function markSectionTimerHandoff(utaId) {
  if (utaId != null) {
    sectionTimerHandoffByUtaId[utaId] = true
  }
}

export function isSectionTimerHandoff(utaId) {
  return utaId != null && !!sectionTimerHandoffByUtaId[utaId]
}

export function clearSectionTimerHandoff(utaId) {
  if (utaId != null) {
    delete sectionTimerHandoffByUtaId[utaId]
  }
}

/** Clears stale alert + section-submit suppress state after handoff / next section. */
export function clearTimedTestHandoffState(utaId) {
  clearTimeEndedAlertOpen(utaId)
  clearSectionTimerHandoff(utaId)
}

export function markAutoSubmitTriggered(utaId) {
  if (utaId != null) {
    autoSubmitTriggeredByUtaId[utaId] = true
  }
}

export function wasAutoSubmitTriggered(utaId) {
  return utaId != null && !!autoSubmitTriggeredByUtaId[utaId]
}
